import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { RepairOrderDialog } from "@/components/RepairOrderDialog";
import { AuditFindingsDialog } from "@/components/AuditFindingsDialog";
import { BatchSummary } from "@/components/BatchSummary";
import { CompareDialog } from "@/components/CompareDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { ocrPage } from "@/lib/ocrPage";
import { groupRoPages, isRoText } from "@/lib/roPages";
import { fmtDate, scoreColor } from "@/lib/roMeta";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ClipboardList, CheckCircle2, AlertTriangle, XCircle, Plus, Pencil, Trash2, Search, Car,
  Upload, FileText, Loader2, FileSpreadsheet, Clock, Wrench, UploadCloud,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Running timer so a long scanned batch visibly keeps working.
function Elapsed() {
  const [s, setS] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setS((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return <span className="ml-auto tabular-nums text-xs text-muted-foreground" data-testid="upload-elapsed">{Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}</span>;
}


// Plain-language reason a file in a batch could not be reviewed.
function failReason(err) {
  const msg = `${err?.name || ""} ${err?.message || ""}`;
  if (err?.message === "timeout") return "Took longer than 20 seconds to check. Try uploading this file on its own.";
  if (/after \d+ tries/.test(err?.message || "")) return "This scanned page could not be read after 3 tries. Upload this file again.";
  if (err?.message === "no-text") return "No readable text found on this page (blank or unreadable scan). Rescan it and re-upload.";
  if (err?.response?.status === 504) return "Reading this scanned page took too long. Try uploading the file again.";
  if (err?.response?.status === 502) return "The scanned page could not be read. Try uploading the file again.";
  if (/PasswordException|password/i.test(msg)) return "This PDF is password-protected. Remove the password and re-upload.";
  if (/InvalidPDF|Invalid PDF|MissingPDF|FormatError|UnexpectedResponse/i.test(msg)) return "Not a valid PDF or the file is damaged. Re-export it from the DMS and re-upload.";
  const status = err?.response?.status;
  if (status === 401) return "Your session expired. Sign in again and re-upload this file.";
  if (status === 413) return "File is too large to upload.";
  if (status) return `Server could not save it (${status}${typeof err.response.data?.detail === "string" ? `: ${err.response.data.detail}` : ""}). Try again.`;
  if (err?.request) return "Network problem while uploading. Check your connection and try again.";
  return `Could not read this PDF${err?.message ? ` (${err.message})` : ""}. Try again.`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [findings, setFindings] = useState([]);
  const [findingsOpen, setFindingsOpen] = useState(false);
  const [failedFiles, setFailedFiles] = useState([]);
  const [importing, setImporting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dmsInputRef = useRef(null);
  const isAdmin = user?.role === "admin";
  const [dealerships, setDealerships] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [visibleCount, setVisibleCount] = useState(150);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Admin's personal board stays own-scoped; dealership users see the shared dealership workspace.
      const scope = user?.role === "admin" ? "?mine=true" : "";
      const [s, o] = await Promise.all([api.get(`/stats${scope}`), api.get(`/repair-orders${scope}`)]);
      setStats(s.data);
      setOrders(o.data);
    } catch {
      toast.error("Failed to load your compliance data");
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin) api.get("/dealerships").then((r) => setDealerships(r.data)).catch(() => {});
  }, [isAdmin]);

  const assignMine = async () => {
    if (!assignTo) return;
    setAssigning(true);
    try {
      const { data } = await api.post("/repair-orders/assign-mine", { dealership_id: assignTo });
      toast.success(`Moved ${data.moved} scan${data.moved === 1 ? "" : "s"} to ${data.dealership}`);
      load();
    } catch {
      toast.error("Couldn't move scans");
    } finally {
      setAssigning(false);
    }
  };

  const profile = {
    dealer: user?.dealership?.name || "Dealership",
    state: user?.dealership?.state || "",
    dms: user?.dealership?.dms || "",
    oem: user?.dealership?.oem || "",
  };

  const processFiles = async (rawFiles) => {
    if (uploading) return;
    if (!rawFiles.length) return;
    setUploading(true);
    setProgress("Loading reader…");
    try {
      const { expandFiles, pdfText, openPdf, pageJpeg } = await import("@/lib/pdfLibs");
      const { auditInBackground } = await import("@/lib/auditRunner");
      const pdfs = await expandFiles(rawFiles);
      if (!pdfs.length) { toast.error("No PDF files were found."); return; }
      const reviewed = [];
      const failures = [];
      const saveAudit = async (text, name, file) => {
        const audit = await auditInBackground(text, name, profile);
        const fd = new FormData();
        fd.append("file", file || new Blob([], { type: "application/pdf" }), name);
        fd.append("audit", JSON.stringify(audit));
        const { data } = await api.post("/repair-orders/ingest", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return data;
      };
      // Scanned PDF: read each scanned page on the server, then group the pages by RO number
      // so a multi-page RO (/1, /2, /3 ...) is saved and audited once.
      const readScanned = async (f, pageTexts, todo) => {
        const pdf = await openPdf(f);
        const pages = pageTexts.map((t) => (isRoText(t) ? t : ""));
        let next = 0, done = 0;
        const worker = async () => {
          while (next < todo.length) {
            const n = todo[next++];
            try {
              const text = await ocrPage(await pageJpeg(pdf, n));
              if (text.replace(/\s/g, "").length < 20) throw new Error("no-text");
              pages[n - 1] = text;
            } catch (err) {
              failures.push({ name: `${f.name} — page ${n}`, reason: failReason(err) });
            }
            setProgress(`Reading scanned pages: ${++done} of ${todo.length} done (${f.name})`);
          }
        };
        setProgress(`Reading scanned pages: 0 of ${todo.length} done (${f.name})`);
        await Promise.all(Array.from({ length: Math.min(8, todo.length) }, worker));
        const groups = groupRoPages(pages);
        for (const [i, g] of groups.entries()) {
          try {
            reviewed.push(await saveAudit(g.text, `Scanned RO ${g.ro || i + 1}.pdf`, null));
          } catch (err) {
            failures.push({ name: `${f.name} — RO ${g.ro || i + 1}`, reason: failReason(err) });
          }
        }
      };
      for (let i = 0; i < pdfs.length; i++) {
        const f = pdfs[i];
        setProgress(`Reviewing ${i + 1} of ${pdfs.length}: ${f.name}`);
        await new Promise((r) => setTimeout(r, 0)); // yield to the browser so the tab stays responsive during large batches
        try {
          const { text, pageTexts, hasImage } = await pdfText(f);
          // Only pages that are scanned images need the slower server reading; typed terms/blank pages are skipped.
          const todo = pageTexts.map((t, i) => (!isRoText(t) && hasImage[i] ? i + 1 : 0)).filter(Boolean);
          if (todo.length) await readScanned(f, pageTexts, todo);
          else reviewed.push(await saveAudit(text, f.name, f));
        } catch (err) {
          failures.push({ name: f.name, reason: failReason(err) });
        }
      }
      const failed = failures.length;
      toast.success(`Reviewed ${reviewed.length} repair order${reviewed.length === 1 ? "" : "s"}${failed ? ` · ${failed} could not be read` : ""}`);
      if (reviewed.length || failed) { setFindings(reviewed); setFailedFiles(failures); setFindingsOpen(true); }
      sessionStorage.removeItem("wcp-reloaded");
      load();
    } catch (err) {
      // A tab opened before an update can't load the new reader files — reload once to pick them up.
      if (/chunk|dynamically imported module|importing a module/i.test(`${err?.name} ${err?.message}`) && !sessionStorage.getItem("wcp-reloaded")) {
        sessionStorage.setItem("wcp-reloaded", "1");
        toast.info("The app was just updated — reloading so you can upload. Please select your files again.");
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(`Upload could not start: ${err?.message || "unknown error"}. Please reload the page and try again.`);
      }
    } finally {
      setUploading(false);
      setProgress("");
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await processFiles(files);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    await processFiles(files);
  };

  const handleDmsImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/repair-orders/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} repair orders from DMS`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const confirmClear = async () => {
    try {
      const { data } = await api.delete("/repair-orders");
      toast.success(`Batch cleared — ${data.deleted} repair order${data.deleted === 1 ? "" : "s"} removed`);
      setClearOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear batch");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/repair-orders/${deleteTarget.id}`);
      toast.success(`RO #${deleteTarget.ro_number} deleted`);
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = orders.filter((o) => {
    const q = query.toLowerCase();
    return !q || [o.ro_number, o.vin, o.customer_name, o.vehicle_make, o.vehicle_model].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <AppLayout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">My Compliance Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Welcome back, {user?.name}. Manage your vehicle repair order audits below.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf,.zip,application/zip"
            multiple
            className="hidden"
            onChange={handleUpload}
            data-testid="pdf-file-input"
          />
          <Button
            variant="outline"
            size="lg"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-pdf-button"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Reviewing…" : "Upload ROs (PDF / ZIP)"}
          </Button>
          <input ref={dmsInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleDmsImport} data-testid="dms-file-input" />
          <Button variant="outline" size="lg" disabled={importing} onClick={() => dmsInputRef.current?.click()} data-testid="dms-import-button">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            {importing ? "Importing…" : "Import DMS CSV"}
          </Button>
          <Button variant="ghost" size="lg" onClick={() => window.open(`${BACKEND}/api/dms/template`, "_blank")} data-testid="dms-template-button">
            Template
          </Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="create-ro-button" size="lg">
            <Plus className="h-4 w-4" /> New Repair Order
          </Button>
          <BatchSummary records={orders} dealership={user?.dealership?.name || user?.dealership} adminName={user?.name} logoUrl={user?.dealership?.has_logo && user?.dealership?.id ? `${process.env.REACT_APP_BACKEND_URL}/api/dealerships/${user.dealership.id}/logo` : null} />
          <CompareDialog records={orders} />
          {orders.length > 0 && (
            <Button variant="outline" size="lg" onClick={() => setClearOpen(true)} data-testid="clear-batch-button"
              className="border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200">
              <Trash2 className="h-4 w-4" /> Clear Batch
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <StatCard testid="stat-total" label="ROs in Batch" value={stats?.total ?? "—"} icon={ClipboardList} tone="primary" sub={stats?.avg_score != null ? `avg score ${stats.avg_score}` : ""} />
        <StatCard testid="stat-ready" label="Ready to Submit" value={stats?.ready_to_submit ?? "—"} icon={CheckCircle2} tone="emerald" />
        <StatCard testid="stat-pending" label="Pending Manual" value={stats?.pending_manual ?? "—"} icon={Clock} tone="primary" />
        <StatCard testid="stat-review" label="Review" value={stats?.review ?? "—"} icon={AlertTriangle} tone="amber" />
        <StatCard testid="stat-required" label="Required" value={stats?.required ?? "—"} icon={XCircle} tone="red" />
        <StatCard testid="stat-skip" label="Skip — Maint." value={stats?.skip_maintenance ?? "—"} icon={Wrench} tone="slate" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={onDrop}
        className={`mt-8 rounded-xl border bg-card/90 shadow-lg transition-colors ${dragging ? "border-primary ring-2 ring-primary/40" : "border-border"}`}
      >
        {(uploading || dragging) && (
          <div className="flex items-center gap-2 border-b border-border bg-primary/5 px-5 py-2.5 text-sm text-primary" data-testid="upload-progress">
            <UploadCloud className="h-4 w-4" />
            {uploading ? (progress || "Reviewing…") : "Drop PDF or ZIP files to review"}
            {uploading && <Elapsed />}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Repair Order Audits</h2>
          {isAdmin && dealerships.length > 0 && (
            <div className="flex items-center gap-2" data-testid="assign-scans-bar">
              <select
                data-testid="assign-dealership-select"
                value={assignTo}
                onChange={(e) => setAssignTo(e.target.value)}
                className="h-9 rounded-md border border-border bg-secondary/40 px-2 text-sm"
              >
                <option value="">Move my scans to…</option>
                {dealerships.map((d) => (
                  <option key={d.id || d._id} value={d.id || d._id}>{d.name}</option>
                ))}
              </select>
              <Button variant="outline" size="sm" disabled={!assignTo || assigning} onClick={assignMine} data-testid="assign-scans-button">
                {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assign"}
              </Button>
            </div>
          )}
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="ro-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search RO, VIN, customer…"
              className="bg-secondary/40 pl-9"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-14 text-center">
            <Car className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No repair order audits yet. Create your first one.</p>
            <Button variant="outline" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="empty-create-ro-button">
              <Plus className="h-4 w-4" /> New Repair Order
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="ro-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider">RO #</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider">Vehicle</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider">Scanned By</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider">Score</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider">Findings</TableHead>
                  <TableHead className="font-mono text-[11px] uppercase tracking-wider">Updated</TableHead>
                  <TableHead className="text-right font-mono text-[11px] uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.slice(0, visibleCount).map((o) => (
                  <TableRow key={o.id} className="border-border" data-testid={`ro-row-${o.ro_number}`}>
                    <TableCell className="font-mono font-semibold text-primary">{o.ro_number}</TableCell>
                    <TableCell className="text-sm">
                      {[o.vehicle_year, o.vehicle_make, o.vehicle_model].filter(Boolean).join(" ") || "—"}
                      <span className="block font-mono text-[10px] text-muted-foreground">{o.vin || "no VIN"}</span>
                    </TableCell>
                    <TableCell className="text-sm">{o.customer_name || "—"}</TableCell>
                    <TableCell className="text-sm" data-testid={`ro-owner-${o.ro_number}`}>
                      {o.owner_name || o.owner_email || "—"}
                      {o.owner_email && user?.email && o.owner_email === user.email && (
                        <span className="ml-1 font-mono text-[10px] uppercase text-primary">you</span>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={o.compliance_status} /></TableCell>
                    <TableCell className={`font-mono text-sm font-bold ${scoreColor(o.score)}`} data-testid={`ro-score-${o.ro_number || o.id}`}>
                      {o.score == null ? "—" : `${o.score}`}
                    </TableCell>
                    <TableCell>
                      {o.flags?.length ? (
                        <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 font-mono text-[11px] text-amber-300" title={o.flags[0]}>
                          <AlertTriangle className="h-3 w-3" /> {o.flags.length}
                        </span>
                      ) : o.compliance_status === "skip_maintenance" ? (
                        <span className="font-mono text-[11px] text-slate-400">maintenance</span>
                      ) : (
                        <span className="font-mono text-[11px] text-emerald-400">clean</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(o.updated_at)}</TableCell>
                    <TableCell className="text-right">
                      {o.source_pdf_path && (
                        <Button variant="ghost" size="icon" className="text-cyan-400 hover:text-cyan-300"
                          onClick={() => window.open(`${BACKEND}/api/repair-orders/${o.id}/pdf`, "_blank")}
                          data-testid={`view-pdf-${o.ro_number}`}>
                          <FileText className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(o); setDialogOpen(true); }} data-testid={`edit-ro-${o.ro_number}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300" onClick={() => setDeleteTarget(o)} data-testid={`delete-ro-${o.ro_number}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filtered.length > visibleCount && (
              <div className="p-4 text-center">
                <Button variant="outline" size="sm" onClick={() => setVisibleCount((n) => n + 200)} data-testid="ro-show-more">
                  Show {Math.min(200, filtered.length - visibleCount)} more ({filtered.length - visibleCount} remaining)
                </Button>
              </div>
            )}
          </div>
        )}
      </motion.div>

      <RepairOrderDialog open={dialogOpen} onOpenChange={setDialogOpen} record={editing} onSaved={load} />

      <AuditFindingsDialog open={findingsOpen} onOpenChange={setFindingsOpen} records={findings} failures={failedFiles} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete repair order?</AlertDialogTitle>
            <AlertDialogDescription>
              RO #{deleteTarget?.ro_number} will be permanently removed. This action is recorded in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
        <AlertDialogContent className="bg-card" data-testid="clear-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear this batch?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes all {orders.length} repair order{orders.length === 1 ? "" : "s"} in your dashboard so you can start a fresh batch. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmClear} className="bg-red-600 hover:bg-red-700" data-testid="clear-confirm-button">Clear Batch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
